import { Autocomplete, TextField } from "@mui/material";
import { DEFAULT_LANGUAGE_CODE } from "features/ScriptManagement/store";
import useGetConfig from "hooks/api/useGetConfig";
import { useMemo } from "react";

interface Props {
  defaultOption?: string;
  label?: string;
  languageCode?: string;
  includeAutoOption?: boolean;
  onChange: (value: string) => void;
}

export const AUTO_LANGUAGE_OPTION = {
  id: "auto",
  name: "(Automatic Detection)",
};

export default function LanguageSelect({
  defaultOption = DEFAULT_LANGUAGE_CODE,
  includeAutoOption,
  label,
  languageCode,
  onChange,
}: Props) {
  const { config } = useGetConfig();

  const options = useMemo(() => {
    const _options = (config?.languages || []).map((language) => ({
      id: language.languageCode,
      name: language.name,
    }));

    includeAutoOption && _options.unshift(AUTO_LANGUAGE_OPTION);

    return _options;
  }, [config, includeAutoOption]);

  const value = useMemo(
    () =>
      options.find((option) => option.id === languageCode) ||
      options.find((option) => option.id === defaultOption),

    [languageCode, options],
  );

  return (
    value && (
      <Autocomplete
        options={options}
        onChange={(_, value) => onChange(value.id)}
        value={value}
        disableClearable
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            InputLabelProps={{
              shrink: true,
              sx: (theme) => ({
                top: -12,
                left: -12,
                ...theme.typography.h6,
              }),
            }}
            sx={({ typography }) => ({
              legend: { display: "none" },
              fieldset: { top: 0 },
              marginTop: typography.pxToRem(20),
            })}
            label={label || "Language"}
          />
        )}
      />
    )
  );
}
